import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { notification, Spin } from "antd";
import { useAppDispatch, useAppSelector } from "../../../../../store/config";
import {
  AbsoluteButton75,
  NotFoundText,
  RelativeFlex,
  StyledColoredHeadline,
  FullWidthFlex,
} from "../../../../../components/common/styles";
import { updateRecipeStart } from "../../../../../store/features/recipe/recipe-slice";
import { ReactComponent as PlusIcon } from "../../../../../assets/icons/plus.svg";
import {
  StyledAddStep,
  StepsWrapper,
  StyledInstructionsNumber,
} from "../../style";
import { StyledTextArea } from "../../../../../components/inputs";

export const RecipeInstructions = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [api, contextHolder] = notification.useNotification();

  const { recipeId } = useParams<{ recipeId: string }>();
  const { isEdit } = location.state || {};

  const [isEditable, setIsEditable] = useState<boolean>(false);
  const [steps, setSteps] = useState<string[]>([]);

  const { recipe, loading, error, requestState, operation } = useAppSelector(
    (state) => state.recipe
  );

  useEffect(() => {
    if (!recipe) return;

    const newSteps =
      recipe.steps?.length > 0 ? recipe.steps : isEditable ? [""] : [];

    setSteps(newSteps);
  }, [recipe, isEditable]);

  useEffect(() => {
    if (isEdit) {
      setIsEditable(true);
    }
  }, [isEdit]);

  useEffect(() => {
    if (requestState === "finish" && operation === "update" && recipe) {
      setIsEditable(false);
    }
  }, [requestState, operation, recipe, navigate]);

  useEffect(() => {
    if (error) {
      openNotification();
    }
  }, [error]);

  const openNotification = (error?: string) => {
    api["error"]({
      message: t(error || "Something went wrong, please try again"),
      placement: "bottomLeft",
    });
  };

  const updateRecipeInstructions = () => {
    if (!isEditable) {
      setIsEditable(true);
      return;
    }

    const filteredSteps = steps.filter((step) => step.trim() !== "");

    if (recipeId) {
      dispatch(
        updateRecipeStart({
          recipe: { steps: filteredSteps },
          recipeId: recipeId,
        })
      );
    }
  };

  const addStep = () => {
    setSteps((prevSteps) => [...prevSteps, ""]);
  };

  const updateStep = (index: number, value: string) => {
    setSteps((prevSteps) => {
      const newSteps = [...prevSteps];
      newSteps[index] = value;
      return newSteps;
    });
  };

  return (
    <>
      {contextHolder}
      <RelativeFlex>
        <AbsoluteButton75 onClick={updateRecipeInstructions}>
          {isEditable ? t("Save") : t("Edit")}
        </AbsoluteButton75>
      </RelativeFlex>

      <Spin spinning={loading}>
        <FullWidthFlex vertical gap={20}>
          {steps.length === 0 && !isEditable ? (
            <NotFoundText>{t("No steps found")}</NotFoundText>
          ) : (
            <StepsWrapper vertical gap={20}>
              {steps.map((step, index) => (
                <FullWidthFlex align="center" gap={10} key={index}>
                  <StyledInstructionsNumber>
                    {index + 1}
                  </StyledInstructionsNumber>
                  <StyledTextArea
                    rows={4}
                    readOnly={!isEditable}
                    value={step}
                    onChange={(e) => updateStep(index, e.target.value)}
                  />
                </FullWidthFlex>
              ))}
              {isEditable && (
                <StyledAddStep
                  justify="end"
                  gap={5}
                  align="center"
                  onClick={addStep}
                >
                  <PlusIcon height={20} width={20} />
                  <StyledColoredHeadline>{t("Add step")}</StyledColoredHeadline>
                </StyledAddStep>
              )}
            </StepsWrapper>
          )}
        </FullWidthFlex>
      </Spin>
    </>
  );
};
